/**
 * Commit scheduler: at most one mount commit per event-loop task.
 *
 * Eager mounts all resume from the same memoized promises (runtime
 * loader, cached component imports), so their commit continuations
 * drain back-to-back as microtasks. Nothing in a microtask cascade
 * yields to the event loop, which turns a page of SSR hydrations
 * into one or two very long main-thread tasks that block input.
 * This scheduler funnels every commit through a FIFO chain and
 * inserts a task boundary between two commits that would otherwise
 * share a task, so the browser can service input, paint and the
 * framework's own render work between components.
 *
 * The yield primitive is a MessageChannel one-shot, deliberately
 * NOT scheduler.yield() or scheduler.postTask():
 *
 * - React's hydrateRoot()/render() only schedule their work; the
 *   actual flush arrives as a MessageChannel task from React's
 *   scheduler. scheduler.yield() continuations jump to the front
 *   of the task queue, so every commit would run before any flush
 *   and React would then process all queued roots in one long
 *   internal loop, exactly the pattern this file exists to break.
 * - Posting on the same message task source as React guarantees
 *   FIFO interleave: commit A, flush A, commit B, flush B, each in
 *   its own task, in every browser.
 *
 * Known limit: a single component whose synchronous render or
 * hydration exceeds the frame budget still produces one long task;
 * that work is framework-internal and sits between two yields.
 */

let sharedPort: MessagePort | null = null;
const pendingResolvers: Array<() => void> = [];

/** Resolves in a later macrotask, FIFO with other message-source tasks. */
export function yieldToMain(): Promise<void> {
  if (typeof MessageChannel !== 'function') {
    return new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  }
  if (!sharedPort) {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => pendingResolvers.shift()?.();
    sharedPort = channel.port2;
    // Node exposes unref() on ports; without it an open listening
    // port keeps the vitest process alive after the suite ends.
    (channel.port1 as unknown as { unref?: () => void }).unref?.();
    (channel.port2 as unknown as { unref?: () => void }).unref?.();
  }
  return new Promise((resolve) => {
    pendingResolvers.push(resolve);
    sharedPort?.postMessage(null);
  });
}

export class CommitScheduler {
  private tail: Promise<unknown> = Promise.resolve();

  private committedInCurrentTask = false;

  /**
   * Runs `commit` on the FIFO chain. A commit that would share a
   * task with a previous commit is deferred to a later macrotask;
   * an isolated commit (empty chain, browser had a turn since the
   * last one) runs immediately with no added latency, which keeps
   * lazy IntersectionObserver mounts as fast as before.
   */
  schedule<T>(commit: () => T): Promise<T> {
    const result = this.tail.then(async () => {
      if (this.committedInCurrentTask) {
        await yieldToMain();
      }
      this.committedInCurrentTask = true;
      try {
        return commit();
      }
      finally {
        // The reset must be a macrotask, not synchronous: after the
        // chain drains, the next mount's load continuation can still
        // arrive in the SAME task via the microtask cascade, which
        // is the exact stacking this scheduler prevents. Any
        // schedule() call observed before this message fires is
        // provably inside the same browser turn. Message-source
        // FIFO orders this reset before a queued follower's yield
        // resumes, so a later commit's flag is never wrongly
        // cleared.
        void yieldToMain().then(() => {
          this.committedInCurrentTask = false;
        });
      }
    });
    // A throwing commit rejects its own caller but must not sever
    // the chain for the commits queued behind it.
    this.tail = result.catch(() => undefined);
    return result;
  }
}
